import React, { useEffect, useState } from 'react';
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom';
import ArrowDown from '../../asset/icon/below.png';
import { infoKo, infoEn, companyKo, companyEn } from '../../asset/policy/footerData';
import { useLanguageStore } from '../../shared/store/languageStore';
import { Language } from '../../shared/enum/EnumRepository';
import LanguageSelector from '../../shared/component/LanguageSelector';

const LeftBottomIsland: React.FC = () => {
  const { pathname } = useLocation();
  const { language } = useLanguageStore();
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const info = language === Language.KR ? infoKo : infoEn;
  const company = language === Language.KR ? companyKo : companyEn;

  useEffect(() => {
    const mainElement = document.querySelector('main');

    //Landing Page에서는 무조건 바로 보이게!
    if (pathname === '/') {
      setIsAtBottom(true);
      return;
    }
    if (!mainElement) return;

    const handleScroll = (el: Element) => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 20;
      setIsAtBottom(isBottom);
    };

    const style = window.getComputedStyle(mainElement);
    const overflow = style.overflow;
    const overflowY = style.overflowY;
    const hasScrollCSS = ['auto', 'scroll', 'overlay'].includes(overflow) ||
      ['auto', 'scroll', 'overlay'].includes(overflowY);
    const hasScrollContent = mainElement.scrollHeight > mainElement.clientHeight;

    if (hasScrollCSS && hasScrollContent) {
      setIsAtBottom(false);
      const boundHandler = () => handleScroll(mainElement);
      mainElement.addEventListener('scroll', boundHandler);
      return () => mainElement.removeEventListener('scroll', boundHandler);
    } else {
      setIsAtBottom(true);
    }
  }, [pathname]);

  return (
    <GnbSection>
      <CompanyInfo $isAtBottom={isAtBottom}>
        <NavigationLinks>
          <NavLink to="/policy/terms">{info.termsOfService}</NavLink>
          <NavLink to="/policy/privacy">{info.privacyPolicy}</NavLink>
          <LanguageSelector />
          <ToggleInfoButton 
            $isInfoOpen={isInfoOpen}
            $backgroundImg={ArrowDown}
            onClick={() => setIsInfoOpen(prev => !prev)}
          />
        </NavigationLinks>
        <InfoWrapper $isInfoOpen={isInfoOpen}>
          <InfoLine>
            <Info>{company.name} |</Info>
            <Info>{info.ceo}: {company.ceo} |</Info>
            <Info>{info.businessNumber}: {company.businessNumber} |</Info>
            <Info>{info.communicationSalesRegistrationNumber}: {company.communicationSalesRegistrationNumber} |</Info>
            <Info>{info.email}: <UnderlinedLink>{company.email}</UnderlinedLink></Info>
          </InfoLine>
          <InfoLine>
            <Info>{info.privacyManager}: {company.privacyManager} |</Info>
            <Info>{info.address}: {company.address} |</Info>
            <Info>{info.phone}: <UnderlinedLink>{company.phone}</UnderlinedLink></Info>
          </InfoLine>
        </InfoWrapper>
      </CompanyInfo>
    </GnbSection>
  );
}

const GnbSection = styled.section`
  position: absolute;
  bottom: 0;
  left: 1vw;
  width: fit-content;
  height: fit-content;
  margin-bottom: 0.3vw;
  
  display: flex;
  align-items: center;
  background-color: transparent;

  user-select: none;
  z-index: 2;
`;

const CompanyInfo = styled.div<{ $isAtBottom: boolean }>`
  display: flex;
  flex-direction: column;
  opacity: ${({ $isAtBottom }) => $isAtBottom ? 0.4 : 0};
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 1;
  }
`;

const NavigationLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-bottom: 4px;
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-decoration: none;
  ${({ theme }) => theme.typography.Footer_01};
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_03};
  }
`;

const ToggleInfoButton = styled.div<{ $isInfoOpen: boolean; $backgroundImg: string }>`
  width: clamp(8px, 1.5vw, 10px);
  height: clamp(8px, 1.5vw, 10px);
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
  cursor: pointer;
  transform: ${({ $isInfoOpen }) => $isInfoOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  opacity: ${({ $isInfoOpen }) => $isInfoOpen ? 0.4 : 0.8};
  transition: opacity 0.3s ease, transform 0.3s ease;
`;

const InfoWrapper = styled.div<{ $isInfoOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  user-select: none;
  height: ${({ $isInfoOpen }) => $isInfoOpen ? '30px' : '0px'};
  transition: height 0.3s ease;
  overflow: hidden;
`;

const InfoLine = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  user-select: none;
`;

const Info = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Footer_02};
`;

const UnderlinedLink = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-decoration: underline;
`;


export default LeftBottomIsland;
